import React, { useEffect, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import Modal from 'ui/layout/Modal'
import { Button, Divider, Link, TextField } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { MedicalInstitutionProps, PublicInformation } from 'types/med'
import { Caption, Text1 } from 'ui/shorthands/Texts'
import { StackItem } from 'ui/layout/StackItem'
import Stack from 'ui/layout/Stack'
import { Controller, useForm, FormProvider } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import TextTagSelect from 'ui/utils/TextTagSelect'
import { StackItemRadioSelect } from 'ui/shorthands/StackItemRadioSelect'
import { QuestionTooltipWithLabel } from 'ui/shorthands/QuestionTooltip'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import validationSchema from 'ui/shorthands/validationSchema'

import placeholder from 'config/placeholder'
import SwitchRadioButton from 'ui/organisms/SwitchRadioButton'

const publicInformationExampleUrl = 'https://support.yadoc.jp/personal/search/'

const allClassificationValues: string[] = [
  '内科',
  '呼吸器科',
  '循環器科',
  '消化器科',
  '放射線科',
  '小児科',
  'アレルギー科',
  '内分泌内科',
  '眼科',
  '精神科',
  '脳神経科',
  '心療内科',
  '皮膚科',
  '整形外科',
  'リウマチ科',
  'リハビリテーション科',
  '産婦人科',
  '耳鼻咽喉科',
  '泌尿器科',
  '乳腺外科',
  '形成外科',
  '性病科',
  '麻酔科',
  '血液内科',
  '歯科',
  '総合診療科',
  '腫瘍内科',
  '緩和ケア',
  '感染症内科',
  '外科',
  'その他診療科',
  '薬局',
]

const allCharacteristicValues: string[] = [
  '電話でも対応可',
  '電話対応のみ',
  '土日診療',
  '女性医師',
  '夜間・時間外対応',
  '英語対応可',
  '中国語対応可',
  'セカンドオピニオン',
  '専門医による診療',
  '当日受付可',
  'クレジットカード決済',
  '銀行振込',
  '着払い',
  'お薬配送可',
  '保険診療',
  '自由診療',
]

const useStyles = makeStyles()((theme: Theme) => ({
  message: {
    display: 'block',
  },
  footer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
  },
  addressCaption: {
    marginTop: theme.spacing(-2),
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  buttons: {
    display: 'flex',
    alignItems: 'center',
  },
  freeText: {
    marginTop: theme.spacing(-4),
  },
  footerCaption: {
    fontSize: 12,
  },
}))

export type PublicInformationModalProps = {
  open: boolean
  med: MedicalInstitutionProps
  defaultValues?: PublicInformation
  onSubmit: (data: PublicInformation) => Promise<void>
  onCancel: () => void
}
const PublicInformationModal: React.FC<PublicInformationModalProps> = ({
  open,
  med,
  defaultValues,
  onSubmit,
  onCancel,
}: PublicInformationModalProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const reservationMethodValues = [
    {
      value: '医療機関ホームページからご予約ください。',
      label: '医療機関ホームページからご予約ください。',
    },
    {
      value: '医療機関にお問い合わせください。',
      label: '医療機関にお問い合わせください。',
    },
    {
      value: 'こちらから予約してください。',
      label: (
        <>
          <Link target="_blank" rel="noopener noreferrer" href={med.form_url}>
            こちら
          </Link>
          から予約してください。
        </>
      ),
      description: (
        <Caption>
          YaDoc Quickの予約スタート画面へのリンクが表示されます。
        </Caption>
      ),
    },
    {
      value: '自由記述',
      label: '自由記述',
    },
  ]

  const schema = yup.object().shape({
    is_request: yup.boolean().required(),
    contact_name: yup
      .object()
      .when('is_request', (is_request: boolean) =>
        is_request
          ? validationSchema.text().required()
          : yup.string().nullable()
      ),
    institution_url: yup.string().url().nullable(),
    is_possible_first_visit: yup
      .object()
      .when('is_request', (is_request: boolean) =>
        is_request ? yup.boolean().required() : yup.boolean().nullable()
      ),
    classification: yup.string().nullable(),
    characteristic: yup.string().nullable(),
    message: yup.string().max(400).nullable(),
    reservation_method: yup
      .object()
      .when('is_request', (is_request: boolean) =>
        is_request ? yup.string().required() : yup.string().nullable()
      ),
    reservation_method_free_description: yup
      .object()
      .when('reservation_method', (reservation_method: string) =>
        reservation_method === '自由記述'
          ? yup.string().max(400).nullable().required()
          : yup.object().nullable()
      ),
  })

  const formMethods = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })
  const {
    handleSubmit,
    formState: { isValid, errors, isDirty },
    reset,
    control,
    watch,
    setValue,
  } = formMethods

  const [sending, setSending] = useState<boolean>(false)

  const watchIsRequest: boolean = watch('is_request')
  const watchReservationMethod: string | null | undefined =
    watch('reservation_method')

  useEffect(() => {
    // NOTE: 選択肢をを変更するたびに自由記述欄は空になる
    if (watchReservationMethod !== '自由記述') {
      setValue('reservation_method_free_description', '', {
        shouldValidate: true,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchReservationMethod])

  const [classificationValues, setClassificationValues] = useState<string[]>(
    defaultValues?.classification?.split(',') || []
  )
  useEffect(() => {
    const result = classificationValues.join(',')
    const defaultValue = defaultValues?.classification
    setValue('classification', result, {
      shouldValidate: true,
      shouldDirty: defaultValue !== result,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classificationValues])

  const [characteristicValues, setCharacteristicValues] = useState<string[]>(
    defaultValues?.characteristic?.split(',') || []
  )
  useEffect(() => {
    const result = characteristicValues.join(',')
    const defaultValue = defaultValues?.characteristic
    setValue('characteristic', result, {
      shouldValidate: true,
      shouldDirty: defaultValue !== result,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [characteristicValues])

  const [keepEmpty, setKeepEmpty] = useState<boolean>(false)
  useEffect(() => {
    // NOTE: 既存データが空であれば送信する必要がない
    setKeepEmpty(!defaultValues?.is_request && !watchIsRequest)

    if (!watchIsRequest) {
      setClassificationValues([])
      setCharacteristicValues([])
      reset({
        is_request: false,
        contact_name: undefined,
        institution_url: undefined,
        is_possible_first_visit: false,
        classification: undefined,
        characteristic: undefined,
        message: undefined,
        reservation_method: undefined,
        reservation_method_free_description: undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchIsRequest])

  const submitHandler = (data: FieldValues) => {
    setSending(true)
    const result = onSubmit(data as PublicInformation)
    result
      .then(() => onCancel())
      .finally(() => {
        setSending(false)
      })
  }

  return (
    <Modal
      large
      open={open}
      handleClose={onCancel}
      footer={
        <div className={classes.footer}>
          <Caption className={classes.footerCaption}>
            {t(
              '変更を送信いただいた後、ウェブサイトへの反映まで約1週間程度かかります。'
            )}
          </Caption>
          <div className={classes.buttons}>
            <MarginRightCancelButton onClick={onCancel} />
            <Button
              variant={'contained'}
              color="primary"
              onClick={handleSubmit(submitHandler)}
              disabled={!isValid || !isDirty || sending || keepEmpty}
            >
              {t('送信')}
            </Button>
          </div>
        </div>
      }
    >
      <>
        <Stack>
          <Text1>
            <Trans
              i18nKey={
                'trans.当社および当社提携企業のウェブサイト上での情報掲載を希望される場合は、...'
              }
              components={{
                Link: (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={publicInformationExampleUrl}
                  />
                ),
              }}
            />
          </Text1>
        </Stack>
        <StackItem label={t('情報掲載')}>
          <FormProvider {...formMethods}>
            <SwitchRadioButton
              name={'is_request'}
              falseLabel={t('希望しない')}
              trueLabel={t('希望する')}
            />
          </FormProvider>
        </StackItem>
        {watchIsRequest && (
          <>
            <Divider variant="middle" className={classes.divider} />
            <StackItem label={t('施設名')}>{med.name}</StackItem>
            <StackItem label={t('電話番号')}>{med.phone}</StackItem>
            <StackItem label={t('住所')}>
              {[
                `〒${med.postal_code}`,
                med.prefecture,
                med.city,
                med.town,
                med.street,
                med.building,
              ].join(' ')}
            </StackItem>
            <Stack className={classes.addressCaption}>
              <Caption>
                {t(
                  '施設名、電話番号、住所の変更は、サポートセンターまでお問い合わせください。'
                )}
              </Caption>
            </Stack>
            <Divider variant="middle" className={classes.divider} />
            <StackItemTextField
              fullWidth
              label={t('管理者氏名')}
              control={control}
              name={'contact_name'}
              placeholder={placeholder().account_name}
              errorMessage={errors?.contact_name?.message}
            />
            <StackItemTextField
              fullWidth
              label={t('ウェブサイトURL(任意)')}
              control={control}
              name={'institution_url'}
              errorMessage={errors?.institution_url?.message}
            />
            <StackItem label={t('オンライン診療初診対応')} alignTop>
              <FormProvider {...formMethods}>
                <SwitchRadioButton
                  name={'is_possible_first_visit'}
                  falseLabel={t('不可')}
                  trueLabel={t('可')}
                />
              </FormProvider>
              <Caption>{t('薬局の場合は、不可を選択してください。')}</Caption>
            </StackItem>
            <StackItem
              label={
                <QuestionTooltipWithLabel label={t('分類(任意)')}>
                  {t(
                    '医療機関検索のときに使用されます。当てはまるものを選択してください。'
                  )}
                </QuestionTooltipWithLabel>
              }
              alignTop
            >
              <TextTagSelect
                values={classificationValues}
                labels={allClassificationValues}
                onChange={setClassificationValues}
              />
              <Controller
                name={'classification'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
            </StackItem>
            <StackItem
              label={
                <QuestionTooltipWithLabel label={t('特徴(任意)')}>
                  {t(
                    '医療機関検索のときに使用されます。当てはまるものを選択してください。'
                  )}
                </QuestionTooltipWithLabel>
              }
              alignTop
            >
              <TextTagSelect
                values={characteristicValues}
                labels={allCharacteristicValues}
                onChange={setCharacteristicValues}
              />
              <Controller
                name={'characteristic'}
                render={({ field }) => <input {...field} type="hidden" />}
                control={control}
              />
            </StackItem>
            <StackItemTextField
              label={
                <QuestionTooltipWithLabel label={t('メッセージ(任意)')}>
                  {t(
                    '受診される方へのメッセージを400字以内でご自由に入力ください。'
                  )}
                </QuestionTooltipWithLabel>
              }
              name={'message'}
              control={control}
              multiline
              alignTop
              fullWidth
              textCounter={400}
            />
            <StackItemRadioSelect
              defaultValue={reservationMethodValues[0].value}
              label={t('予約方法')}
              alignTop
              description={t('表示される文言を選択してください。')}
              control={control}
              name={'reservation_method'}
              values={reservationMethodValues}
            />
            {watchReservationMethod === '自由記述' && (
              <StackItem>
                <div className={classes.freeText}>
                  <Controller
                    name={'reservation_method_free_description'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        error={!!errors?.reservation_method_free_description}
                        helperText={
                          errors?.reservation_method_free_description?.message
                        }
                        fullWidth
                        multiline
                        rows={3}
                      />
                    )}
                  />
                </div>
              </StackItem>
            )}
          </>
        )}
      </>
    </Modal>
  )
}
export default PublicInformationModal
